import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import Image from "../../components/Images/Image";
import { MAX_IMAGE_SIZE } from "../../lib/config";
import {
  showToast,
  startLoader,
  stopLoader,
  UploadImage,
} from "../../lib/global";
import {
  createNewAd,
  getAdById,
  updateAdById,
} from "../../redux/actions/ads.action";
import { breadcrumbAction } from "../../redux/actions/config.action";
import { IOSSwitch } from "../../components/switch/IOSSwitch";

const placementOptions = [
  { value: "top-header", label: "Top Header (1920x70 & 1200x655)", sizeInfo: "Strip: 1920x70 px, Expanded: 1200x655 px" },
  { value: "top-right", label: "Top Right (320x72)", sizeInfo: "320x72 px" },
  { value: "deals-promotion", label: "Deals Promotion (416x216)", sizeInfo: "416x216 px" },
  { value: "promo-banner", label: "Promo Banner (1180x250)", sizeInfo: "1180x250 px" },
];

function AddAd() {
  const { adId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  // Form states
  const [placement, setPlacement] = useState("");
  const [alt, setAlt] = useState("");
  const [href, setHref] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [endDate, setEndDate] = useState(moment().add(1, "month").format("YYYY-MM-DDTHH:mm"));

  // Image file states (base64 string for upload)
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");

  const [stripImageFile, setStripImageFile] = useState(null);
  const [stripImageUrl, setStripImageUrl] = useState("");

  const [expandedImageFile, setExpandedImageFile] = useState(null);
  const [expandedImageUrl, setExpandedImageUrl] = useState("");

  useEffect(() => {
    const breadcrumb = [
      { label: "Ads and Banner", url: "/ads-and-banner" },
      { label: adId ? "Edit Ad" : "Add Ad", url: null },
    ];
    dispatch(breadcrumbAction(breadcrumb));

    if (adId) {
      startLoader();
      dispatch(getAdById(adId))
        .then((res) => {
          stopLoader();
          if (res && res.data && res.data.status === 200) {
            const data = res.data.data;
            setPlacement(data.placement || "top-header");
            setAlt(data.alt || "");
            setHref(data.href || "");
            setIsActive(data.isActive !== undefined ? data.isActive : true);
            if (data.startDate) {
              setStartDate(moment(data.startDate).format("YYYY-MM-DDTHH:mm"));
            }
            if (data.endDate) {
              setEndDate(moment(data.endDate).format("YYYY-MM-DDTHH:mm"));
            }

            if (data.placement === "top-header") {
              setStripImageUrl(data.stripImage || "");
              setExpandedImageUrl(data.expandedImage || "");
            } else {
              setImageUrl(data.image || "");
            }
          }
        })
        .catch((err) => {
          stopLoader();
          console.error("Error fetching ad data:", err);
          showToast("Failed to fetch ad details", "error");
        });
    }
  }, [adId, dispatch]);

  const handleUploadFile = (e, fieldType) => {
    const fileData = e.target.files[0];
    if (!fileData) return;

    if (fileData.size > MAX_IMAGE_SIZE) {
      showToast(
        `Image with size more than ${
          MAX_IMAGE_SIZE / 1000000
        }MB is not supported.\nPlease upload image of size smaller than ${
          MAX_IMAGE_SIZE / 1000000
        }MB.\nCurrent file size: ${(fileData.size / 1000000).toFixed(2)}MB`,
        "error",
        3000,
        "bottom",
        "center"
      );
      return;
    }

    if (fieldType === "strip") {
      setStripImageFile(fileData);
    } else if (fieldType === "expanded") {
      setExpandedImageFile(fileData);
    } else {
      setImageFile(fileData);
    }

    const reader = new FileReader();
    reader.readAsDataURL(fileData);
    reader.onload = function () {
      const base64Data = reader.result;
      if (fieldType === "strip") {
        setStripImageUrl(base64Data);
      } else if (fieldType === "expanded") {
        setExpandedImageUrl(base64Data);
      } else {
        setImageUrl(base64Data);
      }
    };
    reader.onerror = function (error) {
      console.error("FileReader Error: ", error);
    };
  };

  const validateForm = () => {
    if (!placement) {
      showToast("Placement location is required", "error");
      return false;
    }
    if (!alt.trim()) {
      showToast("Alt text is required", "error");
      return false;
    }
    if (!href.trim()) {
      showToast("Target link is required", "error");
      return false;
    }
    if (!startDate) {
      showToast("Start date is required", "error");
      return false;
    }
    if (!endDate) {
      showToast("End date is required", "error");
      return false;
    }
    if (moment(endDate).isBefore(moment(startDate))) {
      showToast("End date cannot be before start date", "error");
      return false;
    }

    if (placement === "top-header") {
      if (!stripImageFile && !stripImageUrl) {
        showToast("Strip image is required", "error");
        return false;
      }
      if (!expandedImageFile && !expandedImageUrl) {
        showToast("Expanded image is required", "error");
        return false;
      }
    } else {
      if (!imageFile && !imageUrl) {
        showToast("Ad image is required", "error");
        return false;
      }
    }

    return true;
  };

  const onSubmit = async () => {
    if (!validateForm()) return;

    startLoader();

    try {
      let finalStripUrl = stripImageUrl;
      let finalExpandedUrl = expandedImageUrl;
      let finalImageUrl = imageUrl;

      // Upload files if new ones are selected
      if (placement === "top-header") {
        if (stripImageFile) {
          const res = await UploadImage(stripImageFile, "ads");
          finalStripUrl = typeof res === "string" ? res : res.secure_url;
        }
        if (expandedImageFile) {
          const res = await UploadImage(expandedImageFile, "ads");
          finalExpandedUrl = typeof res === "string" ? res : res.secure_url;
        }
      } else {
        if (imageFile) {
          const res = await UploadImage(imageFile, "ads");
          finalImageUrl = typeof res === "string" ? res : res.secure_url;
        }
      }

      // Check if upload was successful
      if (placement === "top-header" && (!finalStripUrl || !finalExpandedUrl)) {
        showToast("Image upload failed", "error");
        stopLoader();
        return;
      }
      if (placement !== "top-header" && !finalImageUrl) {
        showToast("Image upload failed", "error");
        stopLoader();
        return;
      }

      const payload = {
        placement,
        alt,
        href,
        isActive,
        startDate: moment(startDate).toISOString(),
        endDate: moment(endDate).toISOString(),
      };

      if (placement === "top-header") {
        payload.stripImage = finalStripUrl;
        payload.expandedImage = finalExpandedUrl;
      } else {
        payload.image = finalImageUrl;
      }

      if (adId) {
        dispatch(updateAdById(adId, payload))
          .then(() => {
            stopLoader();
            showToast("Ad updated successfully", "success");
            history.push("/ads-and-banner");
          })
          .catch((err) => {
            stopLoader();
            console.error("Error updating ad:", err);
            showToast("Failed to update ad", "error");
          });
      } else {
        dispatch(createNewAd(payload))
          .then(() => {
            stopLoader();
            showToast("Ad created successfully", "success");
            history.push("/ads-and-banner");
          })
          .catch((err) => {
            stopLoader();
            console.error("Error creating ad:", err);
            showToast("Failed to create ad", "error");
          });
      }
    } catch (err) {
      stopLoader();
      console.error("Error processing form:", err);
      showToast("Error processing ad data", "error");
    }
  };

  return (
    <React.Fragment>
      <div className="card m-4 p-4 shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
          <h5 className="font-weight-bold text-uppercase mb-0">
            {adId ? "Edit Advertisement" : "Create New Advertisement"}
          </h5>
        </div>

        <div className="row">
          {/* Left Form Column */}
          <div className="col-lg-7">
            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Placement Location</label>
              <select
                className="form-control"
                value={placement}
                onChange={(e) => setPlacement(e.target.value)}
                disabled={!!adId}
              >
                <option value="">-- Select Placement --</option>
                {placementOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {adId && (
                <small className="form-text text-muted">
                  Note: Ad placement location cannot be changed after creation.
                </small>
              )}
            </div>

            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Alt Text</label>
              <input
                type="text"
                placeholder="Description of the ad (for accessibility)"
                value={alt}
                className="form-control"
                onChange={(e) => setAlt(e.target.value)}
              />
            </div>

            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Target Action Link (href)</label>
              <input
                type="text"
                placeholder="e.g. /category/books or /search?sale=true"
                value={href}
                className="form-control"
                onChange={(e) => setHref(e.target.value)}
              />
            </div>

            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="font-weight-bold imp mb-1">Schedule Start Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="font-weight-bold imp mb-1">Schedule End Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group mb-3 d-flex align-items-center mt-2">
              <label className="font-weight-bold mr-3 mb-0">Active Status</label>
              <IOSSwitch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </div>
          </div>

          {/* Right Image Upload Column */}
          <div className="col-lg-5 border-left pl-lg-4">
            <h6 className="font-weight-bold mb-3 text-secondary">BANNER IMAGES</h6>

            {!placement ? (
              <div className="alert alert-warning text-center py-4 rounded-lg my-2 border-0" style={{ backgroundColor: "#fff3cd", color: "#856404" }}>
                <h5 className="font-weight-bold mb-2">!</h5>
                <div className="font-weight-bold mb-1">Please Select Placement First</div>
                <div className="text-muted small">Image uploads are customized based on the selected placement.</div>
              </div>
            ) : placement === "top-header" ? (
              <>
                {/* Collapsed Strip Image */}
                <div className="mb-4">
                  <label className="font-weight-bold imp mb-1 d-block">
                    Strip Image <span className="text-muted">(1920 x 70 px)</span>
                  </label>
                  <input
                    accept="image/*"
                    id="strip-image-upload"
                    className="d-none"
                    onChange={(e) => handleUploadFile(e, "strip")}
                    type="file"
                  />
                  <div className="border rounded p-2 text-center bg-light">
                    <label htmlFor="strip-image-upload" className="pointer m-0 d-block">
                      <Image
                        src={stripImageUrl || "/icons/upload_icon.svg"}
                        alt="strip preview"
                        style={{
                          width: "100%",
                          maxHeight: "60px",
                          objectFit: "contain",
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                      <div className="mt-2 text-primary font-weight-bold small">
                        {stripImageFile || stripImageUrl ? "Change Strip Image" : "Upload Strip Image"}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Expanded Big Image */}
                <div className="mb-4">
                  <label className="font-weight-bold imp mb-1 d-block">
                    Expanded Image <span className="text-muted">(1200 x 655 px)</span>
                  </label>
                  <input
                    accept="image/*"
                    id="expanded-image-upload"
                    className="d-none"
                    onChange={(e) => handleUploadFile(e, "expanded")}
                    type="file"
                  />
                  <div className="border rounded p-2 text-center bg-light">
                    <label htmlFor="expanded-image-upload" className="pointer m-0 d-block">
                      <Image
                        src={expandedImageUrl || "/icons/upload_icon.svg"}
                        alt="expanded preview"
                        style={{
                          width: "100%",
                          maxHeight: "150px",
                          objectFit: "contain",
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                      <div className="mt-2 text-primary font-weight-bold small">
                        {expandedImageFile || expandedImageUrl ? "Change Expanded Image" : "Upload Expanded Image"}
                      </div>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              /* Single Image placements */
              <div className="mb-4">
                <label className="font-weight-bold imp mb-1 d-block">
                  Ad Banner Image{" "}
                  <span className="text-muted">
                    ({placementOptions.find((o) => o.value === placement)?.sizeInfo || ""})
                  </span>
                </label>
                <input
                  accept="image/*"
                  id="standard-image-upload"
                  className="d-none"
                  onChange={(e) => handleUploadFile(e, "standard")}
                  type="file"
                />
                <div className="border rounded p-3 text-center bg-light">
                  <label htmlFor="standard-image-upload" className="pointer m-0 d-block">
                    <Image
                      src={imageUrl || "/icons/upload_icon.svg"}
                      alt="ad preview"
                      style={{
                        width: "100%",
                        maxHeight: "200px",
                        objectFit: "contain",
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                    <div className="mt-2 text-primary font-weight-bold small">
                      {imageFile || imageUrl ? "Change Ad Image" : "Upload Ad Image"}
                    </div>
                  </label>
                </div>
              </div>
            )}
            <div className="text-muted small bg-light p-2 rounded border mt-2">
              <strong>Dimensions requirements:</strong> Upload high quality JPG or PNG matching the exact recommended sizes to ensure best rendering on the storefront. Maximum file size: 3MB.
            </div>
          </div>
        </div>
      </div>

      {/* Footer controls */}
      <footer className="d-flex position-sticky b-0 flex-wrap justify-content-end align-items-center py-3 mt-3 bg-white border-top">
        <div className="col-md-12">
          <div className="d-flex justify-content-end pr-3">
            <button
              type="button"
              className="btn btn-outline-danger px-4 mr-2"
              onClick={history.goBack}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-dark px-4"
              onClick={onSubmit}
            >
              {adId ? "Save Changes" : "Create Advertisement"}
            </button>
          </div>
        </div>
      </footer>
    </React.Fragment>
  );
}

export default AddAd;
